import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Ticket, Loader2, AlertCircle, Train, Calendar, MapPin, User } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Disclaimer from '../components/Disclaimer';
import { usePageMeta } from '../hooks/usePageMeta';

import { API } from '../config';

const PNR_FAQS = [
  {
    q: 'How can I check PNR status online?',
    a: 'Visit the TrainPanda PNR Status page, enter your 10-digit PNR number in the available search field, and submit it to check the latest available reservation information.'
  },
  {
    q: 'What is the best app to check PNR status?',
    a: 'The best option depends on your needs. You can compare TrainPanda, IRCTC Rail Connect, and other railway travel platforms based on ease of use, available features, and the reservation information they provide.'
  },
  {
    q: 'How can I check my IRCTC PNR status?',
    a: 'You can check your IRCTC PNR status through available railway enquiry services or a compatible railway travel platform such as TrainPanda. Enter your 10-digit PNR number and review the latest available status.'
  },
  {
    q: 'How can I know my train ticket confirmation chances?',
    a: 'Confirmation chances depend on your waiting-list category, current position, cancellations, available berths, and reservation quotas. Online predictions are estimates only; check your latest PNR status for updated information.'
  },
  {
    q: 'Can a waiting-list ticket become confirmed?',
    a: 'Yes, a waiting-list ticket may become confirmed if berths become available and the reservation rules permit confirmation. However, confirmation is not guaranteed. Check your PNR status regularly and follow the applicable Indian Railways rules for your ticket category.'
  }
];

const PNRStatus = () => {
  usePageMeta(
    'PNR Status Check Online – Check Train Ticket Status | TrainPanda',
    'Check PNR status online and get the latest train ticket confirmation, RAC or waiting list updates quickly with TrainPanda.'
  );
  const [params] = useSearchParams();
  const [pnr, setPnr] = useState(params.get('pnr') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [source, setSource] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  const fetchPnr = async (p) => {
    if (!p || p.length !== 10) { setError('Please enter a valid 10-digit PNR'); return; }
    setLoading(true); setError(''); setData(null);
    try {
      const r = await axios.get(`${API}/pnr/${p}`);
      setData(r.data.data);
      setSource(r.data.source || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not fetch PNR. Please try again.');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (params.get('pnr')) fetchPnr(params.get('pnr'));
  }, []);

  const statusColor = (s) => {
    if (!s) return 'bg-gray-100 text-gray-700';
    if (s.startsWith('CNF')) return 'bg-green-100 text-green-700';
    if (s.startsWith('RAC')) return 'bg-amber-100 text-amber-700';
    if (s.startsWith('WL')) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div>
      <div className="hero-gradient py-6">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-white mb-4">
            <div className="flex items-center gap-2 text-tp-orange font-semibold text-sm"><Ticket size={16} /> PNR STATUS</div>
            <h1 className="text-2xl md:text-3xl font-bold mt-1">Check PNR confirmation</h1>
          </div>
          <div className="bg-white rounded-xl p-4 flex flex-col sm:flex-row gap-3 soft-shadow">
            <div className="flex items-center gap-2 border-2 border-gray-200 rounded-lg focus-within:border-[#0B2E5C] px-3 flex-1">
              <Ticket size={18} className="text-gray-400" />
              <Input value={pnr} onChange={e=>setPnr(e.target.value.replace(/\D/g,'').slice(0,10))} placeholder="Enter 10-digit PNR number" data-testid="pnr-input" className="border-0 focus-visible:ring-0 h-12 tracking-wider" />
            </div>
            <Button onClick={()=>fetchPnr(pnr)} data-testid="pnr-check-btn" className="bg-tp-orange hover:bg-[#E07A0F] text-white font-semibold h-12 px-8" disabled={loading}>
              {loading ? <Loader2 className="animate-spin" size={18} /> : 'Check PNR'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
            <AlertCircle size={18} /> {error}
          </div>
        )}

        {loading && (
          <div className="text-center py-20">
            <Loader2 className="animate-spin mx-auto text-tp-orange" size={40} />
            <p className="mt-3 text-gray-500">Fetching PNR from TrainPanda Network...</p>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-5">
            <div className="bg-white rounded-2xl p-6 border border-gray-100 card-shadow">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="text-xs text-gray-500">PNR Number</div>
                  <h2 className="text-xl font-bold text-[#0B2E5C] tracking-wider">{data.pnr}</h2>
                </div>
                <Badge className="bg-[#FFF3E6] text-tp-orange border-0 font-semibold">{data.chartStatus}</Badge>
              </div>
              <div className="border-t border-dashed border-gray-200 my-5" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div><div className="text-xs text-gray-500 flex items-center gap-1"><Train size={12} /> Train</div><div className="font-semibold text-[#0B2E5C]">{data.trainNumber}</div><div className="text-xs text-gray-500">{data.trainName}</div></div>
                <div><div className="text-xs text-gray-500 flex items-center gap-1"><Calendar size={12} /> Boarding Date</div><div className="font-semibold text-[#0B2E5C]">{data.boardingDate}</div></div>
                <div><div className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={12} /> From</div><div className="font-semibold text-[#0B2E5C]">{data.from}</div></div>
                <div><div className="text-xs text-gray-500 flex items-center gap-1"><MapPin size={12} /> To</div><div className="font-semibold text-[#0B2E5C]">{data.to}</div></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-4">
                <div><div className="text-xs text-gray-500">Boarding Point</div><div className="font-semibold text-[#0B2E5C]">{data.boardingPoint}</div></div>
                <div><div className="text-xs text-gray-500">Reserved Upto</div><div className="font-semibold text-[#0B2E5C]">{data.reservedUpto}</div></div>
                <div><div className="text-xs text-gray-500">Class</div><div className="font-semibold text-[#0B2E5C]">{data.class}</div></div>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 border border-gray-100 card-shadow">
              <h3 className="font-bold text-[#0B2E5C] mb-4 flex items-center gap-2"><User size={18} className="text-tp-orange" /> Passenger Details</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase border-b">
                      <th className="py-2 pr-4">Passenger</th>
                      <th className="py-2 pr-4">Booking Status</th>
                      <th className="py-2 pr-4">Current Status</th>
                      <th className="py-2 pr-4">Coach Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.passengers?.map((p, i) => (
                      <tr key={i} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-semibold text-[#0B2E5C]">P{p.no}</td>
                        <td className="py-3 pr-4 font-mono text-xs">{p.bookingStatus}</td>
                        <td className="py-3 pr-4"><span className={`px-2 py-1 rounded text-xs font-mono font-semibold ${statusColor(p.currentStatus)}`}>{p.currentStatus}</span></td>
                        <td className="py-3 pr-4 text-gray-500">{p.coachPosition}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {source && <div className="mt-4 text-xs text-gray-400 text-right">Source: TrainPanda Network</div>}
            </div>
          </div>
        )}

        {data && !loading && <Disclaimer />}

        {!data && !loading && !error && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-[#FFF3E6] text-tp-orange mb-4">
              <Ticket size={36} />
            </div>
            <h3 className="text-xl font-bold text-[#0B2E5C]">Instant PNR Status</h3>
            <p className="text-gray-500 text-sm mt-1">Check coach, berth, confirmation chances and chart status.</p>
          </div>
        )}

        {/* SEO & FAQ Content Section from Drive */}
        <section className="mt-12 bg-white rounded-3xl p-6 sm:p-10 border border-gray-100 shadow-sm space-y-8">
          <div>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0B2E5C] tracking-tight">
              Check PNR Confirmation Online with TrainPanda
            </h3>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mt-3">
              Waiting to find out whether your train ticket is confirmed? With TrainPanda, you can conveniently check PNR status online and get the latest available information about your railway reservation. Whether your ticket is Confirmed, RAC, or on the Waiting List, checking your PNR status helps you understand your current booking position before your journey.
            </p>
            <p className="text-sm sm:text-base text-gray-600 leading-relaxed mt-2">
              If you are searching for the best app to check PNR status, a reliable website for an IRCTC PNR check, or an easy way to find your railway ticket status, TrainPanda provides a convenient starting point. Visit the TrainPanda PNR Status page to check your reservation details and prepare for your upcoming train journey.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              What Is PNR Status in Indian Railways?
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              PNR stands for Passenger Name Record. It is a unique 10-digit number associated with a railway reservation. Passengers use this number to check booking details and the current status of their train tickets.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Your PNR status indicates whether your reservation is confirmed, remains under the Reservation Against Cancellation (RAC) category, or is still on the Waiting List. The status can change as passengers cancel their tickets and available berths are allocated.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Checking your railway ticket status regularly is particularly useful when you have booked a waiting-list ticket. However, confirmation is not guaranteed, and the final outcome depends on factors such as cancellations, booking quotas, seat availability, and railway reservation rules.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              How to Check PNR Status Online with TrainPanda
            </h4>
            <p className="text-sm text-gray-600">
              Checking your reservation status online is simple. Follow these steps to get started:
            </p>
            <ol className="list-decimal list-inside text-sm text-gray-600 space-y-1.5 pl-1">
              <li>Visit the TrainPanda PNR Status page.</li>
              <li>Find the available PNR search option.</li>
              <li>Enter your 10-digit PNR number in the required field.</li>
              <li>Submit the details to check your reservation status.</li>
              <li>Review the latest available information about your ticket.</li>
            </ol>
            <p className="text-sm text-gray-600 leading-relaxed mt-2">
              Before travelling, check your PNR status again because reservation details may change before chart preparation.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Understand Your Train Ticket Confirmation Status
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              When you check PNR status, you may come across different reservation categories. Understanding them can help you plan your journey more effectively.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
              <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-100">
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-200/80 text-emerald-900 mb-2">Confirmed</span>
                <h5 className="font-bold text-gray-900 text-sm mb-1">Confirmed Ticket Status</h5>
                <p className="text-xs text-gray-600 leading-relaxed">
                  A confirmed ticket means a seat or berth has been allotted according to your reservation details. Check the coach and berth information available on your ticket before boarding.
                </p>
              </div>
              <div className="p-4 rounded-2xl bg-amber-50 border border-amber-100">
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-amber-200/80 text-amber-900 mb-2">RAC</span>
                <h5 className="font-bold text-gray-900 text-sm mb-1">RAC Ticket Status</h5>
                <p className="text-xs text-gray-600 leading-relaxed">
                  RAC stands for Reservation Against Cancellation. Passengers with RAC status are generally permitted to travel, subject to the applicable railway rules, although a full berth may not initially be allotted.
                </p>
              </div>
              <div className="p-4 rounded-2xl bg-rose-50 border border-rose-100">
                <span className="inline-block px-2.5 py-1 rounded-full text-xs font-bold bg-rose-200/80 text-rose-900 mb-2">Waitlist</span>
                <h5 className="font-bold text-gray-900 text-sm mb-1">Waiting List Ticket Status</h5>
                <p className="text-xs text-gray-600 leading-relaxed">
                  A waiting-list ticket means your reservation has not yet been confirmed. Its status may change as seats become available. Confirmation chances depend on the waiting-list category, cancellations, quota, and other reservation factors.
                </p>
              </div>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed mt-2">
              If you want to understand your train ticket confirmation chances, check the latest PNR information instead of relying entirely on an earlier prediction.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Best App to Check PNR Status of IRCTC
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Many passengers search online for the best app to check PNR status, the best app for an IRCTC PNR check, or a convenient website to check railway ticket status. The right option should be easy to use, provide relevant reservation information, and work conveniently on mobile devices.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              TrainPanda offers a website-based option for passengers who want to check their PNR status without making the process complicated. You can visit the PNR page using a mobile phone, tablet, or computer.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              When comparing the top 10 PNR status apps for Indian Railways or the top 10 PNR status sites for Indian Railways, consider the ease of searching, clarity of information, and availability of updated reservation details. TrainPanda is one platform you can explore for your PNR status checking needs.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Features and services vary across these platforms. Some focus on ticket booking, while others offer train tracking, PNR enquiries, or journey-planning tools. Check each service's current features and official information before choosing one.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Why Check PNR Status Before Travelling?
            </h4>
            <p className="text-sm text-gray-600">
              Checking your PNR status before departure helps you stay informed about your reservation and avoid unnecessary uncertainty. It allows you to review whether your ticket is confirmed, RAC, or waitlisted and make suitable travel arrangements based on the latest information.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              For passengers with waiting-list bookings, regular checks are especially useful because reservation status can change as the journey date approaches. Remember that estimated confirmation chances are not guarantees of a confirmed ticket.
            </p>
          </div>

          <div className="pt-4 border-t border-gray-100 space-y-3">
            <h4 className="text-lg sm:text-xl font-bold text-[#0B2E5C]">
              Check Your PNR Status with TrainPanda
            </h4>
            <p className="text-sm text-gray-600 leading-relaxed">
              Make your railway journey easier by checking your reservation before you leave. Whether you need an IRCTC PNR check, want to know your railway ticket status, or are looking for an easy way to check PNR status online, TrainPanda helps you get started.
            </p>
            <p className="text-sm font-semibold text-tp-violet">
              Check your PNR status today: <a href="https://www.trainpanda.com/pnr-status" className="underline hover:text-tp-violet/80">https://www.trainpanda.com/pnr-status</a>
            </p>
          </div>

          {/* FAQ Section */}
          <div className="pt-6 border-t border-gray-200">
            <h4 className="text-xl font-bold text-[#0B2E5C] mb-4">
              Frequently Asked Questions (FAQs)
            </h4>
            <div className="space-y-3">
              {PNR_FAQS.map((faq, idx) => (
                <div key={idx} className="border border-gray-200/80 rounded-2xl overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full px-5 py-4 text-left font-bold text-gray-800 flex items-center justify-between gap-4 hover:text-tp-violet transition-colors"
                  >
                    <span>{idx + 1}. {faq.q}</span>
                    <span className="text-xl text-gray-400 font-light select-none">
                      {openFaq === idx ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PNRStatus;
